import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { action, computed, observable, runInAction } from 'mobx'
import {
  type DocumentStore,
  type NewDocumentAccessOptions,
  type UploadDocumentProgress,
} from '@/features/docs/DocumentStore'
import type { PromptAssignFileTypesFn } from '@/features/docs/components/AssignFileTypeDialog/AssignFileTypeDialogViewModel'
import type { Document } from '@/features/docs/Document'
import type { DocumentType } from '@/features/docs/DocumentType'
import type { GetCurrentWorkspaceFn } from '@/features/iam'
import { type Observable, Subject } from 'rxjs'
import { tapAction } from '@/utils/mobx-util'
import { removeElement } from '@/utils/array-util'
import { isAbortError } from '@/utils/abort-util'
import type { SnackbarFactory } from '@/features/shell'
import { uniqueId } from 'lodash-es'
import type { Id } from '@/infra/id'

/**
 * Options for {@link DocumentUploaderViewModel}.
 */
export interface DocumentUploaderViewModelOptions {
  /**
   * When uploading new documents, an access can be associated with them
   * immediately.
   */
  access?: NewDocumentAccessOptions | null
}

/**
 * Manages state for uploading documents.
 */
export class DocumentUploaderViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Subject where newly uploaded documents will be emitted.
   *
   * @private
   */
  private readonly _uploaded$ = new Subject<Document>()

  /**
   * Documents being uploaded.
   */
  @observable accessor pendingDocuments: PendingDocumentViewModel[] = []

  /**
   * Emits newly uploaded documents.
   */
  readonly uploaded$ = this._uploaded$.asObservable()

  constructor(
    private readonly _options: DocumentUploaderViewModelOptions,
    private readonly _getCurrentWorkspace: GetCurrentWorkspaceFn,
    private readonly _newSnackbar: SnackbarFactory,
    private readonly _documentStore: DocumentStore,
    private readonly _promptAssignFileTypes: PromptAssignFileTypesFn,
  ) {
    this.upload = this.upload.bind(this)
  }

  /**
   * A set of {@link Document}s that are currently being uploaded.
   *
   * @private
   */
  @computed
  private get _uploadingDocs() {
    return new Set(
      this.pendingDocuments.flatMap((vm) => (vm.document ? [vm.document] : [])),
    )
  }

  /**
   * Checks whether the specified {@link doc} is currently being uploaded.
   *
   * @param doc
   */
  isUploading(doc: Document) {
    return this._uploadingDocs.has(doc)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Asks the user to assign types to and starts
   * uploading the specified files.
   *
   * @param files
   * @param documentTypeId If specified, uses this type ID without
   * prompting the user.
   */
  async upload(files: File[], documentTypeId?: Id) {
    const MAX_FILE_COUNT = 100
    if (files.length > MAX_FILE_COUNT) {
      this._newSnackbar((s) =>
        s
          .type('error')
          .saying(
            'Too many files',
            `You can upload at most ${MAX_FILE_COUNT} files at once.`,
          )
          .autoDismiss()
          .show(),
      )
      return
    }

    // Filter out files that exceed the 10mb limit.
    const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024 // 10 MB
    const filesExceedingLimit: File[] = []
    const filesWithoutExtension: File[] = []
    const filesToUpload: File[] = []
    for (const file of files) {
      const target = !file.name.includes('.')
        ? filesWithoutExtension
        : file.size > MAX_FILE_SIZE_BYTES
          ? filesExceedingLimit
          : filesToUpload
      target.push(file)
    }

    if (filesWithoutExtension.length > 0) {
      this._newSnackbar((s) =>
        s
          .type('error')
          .saying(
            'File extensions are required',
            `The following files are missing file extensions: ${filesWithoutExtension.map((f) => f.name).join(', ')}`,
          )
          .autoDismiss()
          .show(),
      )
    }

    if (filesExceedingLimit.length > 0) {
      this._newSnackbar((s) =>
        s
          .type('error')
          .saying(
            'Some files were too large',
            `The following files exceed the 10MB file size limit: ${filesExceedingLimit.map((f) => f.name).join(', ')}`,
          )
          .autoDismiss()
          .show(),
      )
    }

    if (!filesToUpload.length) {
      return
    }

    // Ask the user to assign a type to each file.
    // If the `documentTypeId` is specified, it'll skip
    // prompting the user.
    const assigned = await this._promptAssignFileTypes(
      filesToUpload,
      documentTypeId,
      this._lifecycle.signal,
    )

    if (!assigned || assigned.size === 0 || this._lifecycle.signal.aborted) {
      // Canceled by the user or aborted due to navigating away
      return
    }

    const snackbar = this._newSnackbar((s) =>
      s
        .type('pending')
        .saying(
          assigned.size === 1
            ? `Uploading document`
            : `Uploading ${assigned.size} documents`,
        )
        .show(),
    )

    const results = await Promise.all(
      Array.from(assigned.entries()).map(([file, type]) =>
        this._uploadFile(file, type),
      ),
    )

    const errors = results.filter(
      (r) => r.type === 'Failed' && !isAbortError(r.error),
    )

    if (errors.length) {
      snackbar.update((s) =>
        s
          .type('error')
          .saying('There was a problem uploading some of your documents.'),
      )
    } else {
      snackbar.update((s) =>
        s
          .type('success')
          .saying(
            assigned.size === 1
              ? 'Your document was uploaded successfully.'
              : `${assigned.size} documents were uploaded successfully`,
          )
          .autoDismiss(),
      )
    }
  }

  /**
   * Uploads a single file.
   */
  private async _uploadFile(
    file: File,
    type: DocumentType | null,
  ): Promise<UploadResult> {
    const pendingVm = new PendingDocumentViewModel(file, type)
    this._lifecycle.manage(pendingVm)

    runInAction(() => this.pendingDocuments.push(pendingVm))

    try {
      const uploadObserver = this._documentStore.upload({
        file,
        access: this._options.access,
        // Explicitly not using a signal here because we want
        // the upload to continue even if we leave the screen
        // hosting the view model.
        signal: undefined,
        documentTypeId: type?.id,
        workspaceId: this._getCurrentWorkspace().id,
      })

      const document = await uploadObserver.created
      pendingVm.onUploadStarting(document, uploadObserver.progress$)
      await uploadObserver.completed
      this._uploaded$.next(document)
      return { type: 'Success', document }
    } catch (error: unknown) {
      return { type: 'Failed', error }
    } finally {
      runInAction(() => removeElement(this.pendingDocuments, pendingVm))
      this._lifecycle.remove(pendingVm)
    }
  }
}

/**
 * Upload result.
 */
export type UploadResult =
  | { type: 'Success'; document: Document }
  | { type: 'Failed'; error: unknown }

/**
 * A document that is in the process of being uploaded.
 */
export class PendingDocumentViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Client-side ID.
   */
  readonly cid = uniqueId('pending-doc')

  /**
   * The backing document, after it has been uploaded.
   */
  @observable accessor document: Document | null = null

  /**
   * How much has been uploaded so far.
   */
  @observable accessor progressUploaded = 0

  /**
   * Total progress value - that is, when {@link progressUploaded} is
   * equal to this, the upload is effectively completed.
   */
  @observable accessor progressTotal = 1

  constructor(
    // The DOM file object to be uploaded.
    private readonly _file: File,
    public readonly type: DocumentType | null,
  ) {}

  /**
   * Gets the name of the file being uploaded.
   */
  @computed
  get fileName() {
    return this._file.name
  }

  /**
   * The progress in percentage form.
   */
  @computed
  get progressPercent(): number {
    return Math.round((this.progressUploaded / this.progressTotal) * 100)
  }

  /**
   * Sets the document so we know where the file being uploaded
   * belongs.
   *
   * @param document The document that was created.
   * @param progress$ Progress observable.
   */
  @action.bound
  onUploadStarting(
    document: Document,
    progress$: Observable<UploadDocumentProgress>,
  ) {
    this.document = document

    this._lifecycle.manage(
      progress$.pipe(
        tapAction((progress) => {
          this.progressUploaded = progress.uploaded
          this.progressTotal = progress.total
        }),
      ),
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }
}
